import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './Epk.css';

const PRESS_PHOTO_DIR = '/images/press';
const BOOKING_EMAIL = 'echohillinfo@gmail.com';
const BOOKING_MAILTO = `mailto:${BOOKING_EMAIL}?subject=${encodeURIComponent('Booking inquiry: Echo Hill')}`;

const FACTS = [
  { label: 'Genre', value: 'Alternative / Desert / Prog Rock' },
  { label: 'Hometown', value: 'Sacramento, CA' },
  { label: 'Lineup', value: '4-piece — 2 guitars, bass, drums, 2 vocalists' },
  { label: 'Latest Release', value: 'Echo Hill (debut LP) — 11 tracks, Feb 2026' },
];

const PRESS = {
  quotes: [
    'These guys (and a girl) know a thing or two about shredding.',
    'Their strong, melodic sound was just what the crowd needed\u2026 Their set was exciting and kept the crowd motivated.',
  ],
  source: 'The Little Music Magazine',
  context: 'Live review — The Cinders, Sacramento, October 3, 2026',
  url: 'https://bymatte.com/2026/10/07/echo-hill-put-on-an-amazing-set-at-the-cinders-in-sacramento-california-october-3-2026/',
};

const SHORT_BIO =
  'Echo Hill is a Sacramento four-piece blending atmospheric alternative rock, desert groove, and cinematic ' +
  'progressive dynamics into emotionally immersive heavy soundscapes. Their self-titled debut LP is out now.';

const MEMBERS = [
  { name: 'Gene Smith', role: 'Vocals, Guitar' },
  { name: 'Curtis Harvey', role: 'Guitar' },
  { name: 'Amy Weaver', role: 'Vocals, Bass' },
  { name: 'Steve DeCristofaro', role: 'Drums' },
];

const STREAMING = [
  { label: 'Bandcamp', href: 'https://echohillband.bandcamp.com/album/echo-hill' },
  { label: 'Spotify', href: 'https://open.spotify.com/artist/63z9YSnIfEs8ycriPWBWOU' },
  { label: 'Apple Music', href: 'https://music.apple.com/us/artist/echo-hill/1882167530' },
  { label: 'Amazon Music', href: 'https://music.amazon.com/albums/B0GR2R3FKJ' },
];

const SOCIAL = [
  { label: 'Instagram', handle: '@echohill.band', href: 'https://www.instagram.com/echohill.band' },
  { label: 'Facebook', handle: 'weareechohill', href: 'https://www.facebook.com/weareechohill' },
  { label: 'YouTube', handle: '@EchoHillBand916', href: 'https://www.youtube.com/@EchoHillBand916' },
  { label: 'Bandsintown', handle: 'Echo Hill', href: 'https://www.bandsintown.com/a/echo-hill' },
];

const BANDCAMP_EMBED = 'https://bandcamp.com/EmbeddedPlayer';
const BANDCAMP_THEME = 'bgcol=333333/linkcol=c94a1a/transparent=true';

const FEATURED_TRACKS = [
  {
    title: 'Return of the Gods',
    trackId: 3494995362,
    href: 'https://echohillband.bandcamp.com/track/return-of-the-gods',
    blurb: 'The expansive, atmospheric, progressive side of the band.',
  },
  {
    title: 'crash.override',
    trackId: 2942125661,
    href: 'https://echohillband.bandcamp.com/track/crash-override',
    blurb: 'The album opener, showing another dimension of the band\u2019s range.',
  },
  {
    title: 'Cylinders Firing',
    trackId: 2352876320,
    href: 'https://echohillband.bandcamp.com/track/cylinders-firing',
    blurb: 'An uptempo instrumental with drive and forward momentum.',
  },
];

const LIVE_VIDEOS = [
  { id: 'dJTFe8i0GMw', title: 'Live @ Cafe Colonial — Feb 27, 2026' },
  { id: 'HJMcQ5PV4t8', title: 'Live @ The Torch Club — Feb 8, 2025' },
];

const LYRIC_VIDEOS = [
  { id: 'v7_XeZeqOVo', title: 'A Life Not Lived' },
  { id: 'C1yhMV-PcN0', title: 'Fight for Dominion' },
  { id: 'f4RZPUEb1sM', title: 'Cylinders Firing' },
  { id: 'u5gpjPBvfpg', title: 'Return of the Gods' },
];

const PHOTOS = [
  { file: 'echo-hill-band-promo-photo.jpg', caption: 'Echo Hill \u2014 promo photo', size: '4000\u00d71848' },
  { file: 'echo-hill-live-the-cinders-sacramento.jpg', caption: 'Live at The Cinders, Sacramento', size: '5752\u00d73236' },
  { file: 'echo-hill-live-cafe-colonial-sacramento.jpg', caption: 'Live at Cafe Colonial, Sacramento', size: '1440\u00d71080' },
  { file: 'echo-hill-live-full-band-1.jpg', caption: 'Live \u2014 full band', size: '2048\u00d71365' },
  { file: 'echo-hill-live-full-band-2.jpg', caption: 'Live \u2014 full band', size: '1438\u00d71146' },
  { file: 'echo-hill-live-full-band-3.jpg', caption: 'Live \u2014 full band', size: '3120\u00d72080' },
  { file: 'echo-hill-gene-smith-vocals-guitar.jpg', caption: 'Gene Smith \u2014 vocals, guitar', size: '2160\u00d71440' },
  { file: 'echo-hill-curtis-harvey-guitar.jpg', caption: 'Curtis Harvey \u2014 guitar', size: '2160\u00d71440' },
  { file: 'echo-hill-amy-weaver-vocals-bass.jpg', caption: 'Amy Weaver \u2014 vocals, bass', size: '2160\u00d71440' },
  { file: 'echo-hill-steve-decristofaro-drums.jpg', caption: 'Steve DeCristofaro \u2014 drums', size: '2160\u00d71440' },
];

const PROMO_PHOTO = PHOTOS[0];

// Newest first. Event links point to Bandsintown.
const RECENT_SHOWS = [
  {
    date: 'Oct 3, 2026', venue: 'The Cinders', city: 'Sacramento, CA',
    note: 'w/ The Streetwalkin\u2019 Cheetahs, Screaming Bloody Marys, Banger',
    href: 'https://www.bandsintown.com/e/108500623',
  },
  {
    date: 'Aug 14, 2026', venue: 'The San Juan Club', city: 'Fair Oaks, CA',
    note: 'w/ Horton\u2019z Heroz',
    href: 'https://www.bandsintown.com/e/108468120',
  },
  {
    date: 'May 29, 2026', venue: 'Castle Budwak', city: 'Sacramento, CA',
    note: 'All-ages bill w/ Blue Elephant and the 7 Snakes, Freshman Cannibals, Bret Koozan',
    href: 'https://www.bandsintown.com/e/108312282',
  },
  {
    date: 'Feb 27, 2026', venue: 'Cafe Colonial', city: 'Sacramento, CA',
    note: 'w/ Human Body, Black Knight Satellite',
    href: 'https://www.bandsintown.com/e/107788581',
  },
  {
    date: 'Dec 19, 2025', venue: 'Harlow\u2019s', city: 'Sacramento, CA',
    note: 'Celebration of Life for Charles \u201cGround Chuck\u201d Thomas \u2014 benefit for Chalk It Up!',
    href: 'https://www.bandsintown.com/e/107566997',
  },
  {
    date: 'Nov 14, 2025', venue: 'The San Juan Club', city: 'Fair Oaks, CA',
    href: 'https://www.bandsintown.com/e/107566986',
  },
];

export default function Epk() {
  return (
    <div className="epk">
      <Helmet>
        <title>Electronic Press Kit | Echo Hill</title>
        <meta name="description" content="Echo Hill electronic press kit: bio, music, live video, press photos, and booking contact." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Hero */}
      <header className="epk-hero">
        <div className="epk-hero-image">
          <img src="/images/band-photo.jpg" alt="Echo Hill" />
          <div className="epk-hero-gradient" />
        </div>
        <div className="epk-hero-content">
          <p className="epk-eyebrow">Electronic Press Kit</p>
          <img src="/images/logo.png" alt="Echo Hill" className="epk-hero-logo" />
          <p className="epk-hero-tagline">Heavy alternative rock, desert grooves, and cinematic progressive dynamics.</p>
          <div className="epk-cta-row">
            <a href={BOOKING_MAILTO} className="epk-btn">Book Echo Hill</a>
            <a href="#listen" className="epk-btn epk-btn--outline">Listen</a>
            <a href="#live" className="epk-btn epk-btn--outline">Watch Live</a>
          </div>
        </div>
      </header>

      <div className="section-container epk-body">
        {/* Quick facts */}
        <section className="epk-facts">
          {FACTS.map(({ label, value }) => (
            <div key={label} className="epk-fact">
              <span className="epk-fact-label">{label}</span>
              <span className="epk-fact-value">{value}</span>
            </div>
          ))}
          <div className="epk-fact">
            <span className="epk-fact-label">Booking</span>
            <a href={BOOKING_MAILTO} className="epk-fact-value epk-fact-link">{BOOKING_EMAIL}</a>
          </div>
        </section>

        {/* Press */}
        <section className="epk-section">
          <SectionHeading>Press</SectionHeading>
          <div className="epk-press">
            {PRESS.quotes.map((q) => (
              <blockquote key={q} className="epk-quote">&ldquo;{q}&rdquo;</blockquote>
            ))}
            <p className="epk-press-source">
              &mdash; <strong>{PRESS.source}</strong>, {PRESS.context}{' '}
              <a href={PRESS.url} target="_blank" rel="noopener noreferrer">Read the review ↗</a>
            </p>
          </div>
        </section>

        {/* Bio */}
        <section className="epk-section">
          <SectionHeading>Bio</SectionHeading>
          <div className="epk-bio-grid">
            <div className="epk-bio">
              <p className="epk-bio-lead">
                Echo Hill is a Sacramento-based alternative rock band blending heavy riffs, hypnotic desert
                grooves, atmospheric textures, and progressive dynamics. The result is a sound that moves
                between driving heaviness and expansive, cinematic passages.
              </p>
              <p>
                With a powerful live sound and a dynamic approach to songwriting, Echo Hill bridges alternative
                rock, desert rock, and progressive heaviness. The band is available for club shows, support
                slots, and regional bookings.
              </p>
            </div>
            <aside className="epk-sidebar">
              <div className="epk-card">
                <p className="epk-card-label">Short bio (for listings)</p>
                <p className="epk-card-text">{SHORT_BIO}</p>
              </div>
              <div className="epk-card">
                <p className="epk-card-label">Lineup</p>
                <ul className="epk-members">
                  {MEMBERS.map(({ name, role }) => (
                    <li key={name}>
                      <span className="epk-member-name">{name}</span>
                      <span className="epk-member-role">{role}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>

        {/* Listen */}
        <section className="epk-section" id="listen">
          <SectionHeading>Listen</SectionHeading>
          <div className="epk-listen-grid">
            <div className="epk-player">
              <iframe
                className="epk-player-frame"
                src={`${BANDCAMP_EMBED}/album=3277359218/size=large/${BANDCAMP_THEME}/artwork=small/`}
                seamless="seamless"
                allow="autoplay"
                title="Echo Hill on Bandcamp"
              >
                <a href="https://echohillband.bandcamp.com/album/echo-hill">Echo Hill by Echo Hill</a>
              </iframe>
            </div>
            <aside className="epk-featured">
              <p className="epk-card-label">Start here &mdash; featured tracks</p>
              <ol className="epk-featured-list">
                {FEATURED_TRACKS.map(({ title, trackId, href, blurb }, i) => (
                  <li key={trackId} className="epk-featured-track">
                    <div className="epk-featured-head">
                      <span className="epk-featured-num">{String(i + 1).padStart(2, '0')}</span>
                      <a href={href} target="_blank" rel="noopener noreferrer" className="epk-featured-title">
                        {title} ↗
                      </a>
                    </div>
                    <p className="epk-featured-blurb">{blurb}</p>
                    <iframe
                      className="epk-featured-player"
                      src={`${BANDCAMP_EMBED}/track=${trackId}/size=small/${BANDCAMP_THEME}/`}
                      seamless="seamless"
                      allow="autoplay"
                      title={`${title} by Echo Hill on Bandcamp`}
                    >
                      <a href={href}>{title} by Echo Hill</a>
                    </iframe>
                  </li>
                ))}
              </ol>
            </aside>
          </div>
          <div className="epk-link-row">
            {STREAMING.map(({ label, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="epk-pill">
                {label} ↗
              </a>
            ))}
          </div>
        </section>

        {/* Live video */}
        <section className="epk-section" id="live">
          <SectionHeading>Live Video</SectionHeading>
          <div className="epk-video-grid">
            {LIVE_VIDEOS.map((v) => <LiteYouTube key={v.id} {...v} />)}
          </div>
          <p className="epk-subheading">Music videos</p>
          <div className="epk-video-grid epk-video-grid--small">
            {LYRIC_VIDEOS.map((v) => <LiteYouTube key={v.id} {...v} />)}
          </div>
          <div className="epk-link-row">
            <a href="https://www.youtube.com/@EchoHillBand916" target="_blank" rel="noopener noreferrer" className="epk-pill">
              All videos on YouTube ↗
            </a>
          </div>
        </section>

        {/* Photos */}
        <section className="epk-section">
          <SectionHeading>Press Photos</SectionHeading>
          <p className="epk-note">
            Full-resolution JPGs, free to use for show promotion. Use the Download link on any photo, or
            click/tap a photo to open it full size.
          </p>
          <div className="epk-photo-feature">
            <a
              href={`${PRESS_PHOTO_DIR}/${PROMO_PHOTO.file}`}
              download={PROMO_PHOTO.file}
              className="epk-btn"
            >
              Download hi-res band photo
            </a>
            <span className="epk-photo-feature-meta">JPG &middot; {PROMO_PHOTO.size}</span>
          </div>
          <div className="epk-photo-grid">
            {PHOTOS.map(({ file, caption, size }) => (
              <figure key={file} className="epk-photo">
                <a href={`${PRESS_PHOTO_DIR}/${file}`} target="_blank" rel="noopener noreferrer" className="epk-photo-link">
                  <img src={`${PRESS_PHOTO_DIR}/thumbs/${file}`} alt={caption} loading="lazy" />
                </a>
                <figcaption className="epk-photo-caption">
                  <span>{caption}</span>
                  <a href={`${PRESS_PHOTO_DIR}/${file}`} download={file} className="epk-photo-dl" aria-label={`Download ${caption} (${size} JPG)`}>
                    Download <span className="epk-photo-size">{size}</span>
                  </a>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="epk-logo-row">
            <a href="/images/logo.png" download="echo-hill-logo.png" className="epk-logo-dl">
              <img src="/images/logo.png" alt="Echo Hill logo" />
              <span>Download logo (PNG, 1683\u00d7541)</span>
            </a>
          </div>
        </section>

        {/* Shows */}
        <section className="epk-section">
          <SectionHeading>Recent Shows</SectionHeading>
          <ul className="epk-shows">
            {RECENT_SHOWS.map(({ date, venue, city, note, href }) => (
              <li key={date + venue} className="epk-show">
                <span className="epk-show-date">{date}</span>
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="epk-show-venue">{venue}</a>
                ) : (
                  <span className="epk-show-venue">{venue}</span>
                )}
                <span className="epk-show-city">{city}</span>
                {note && <span className="epk-show-note">{note}</span>}
              </li>
            ))}
          </ul>
          <div className="epk-link-row">
            <a href="/shows" className="epk-pill">Upcoming dates</a>
            <a href="https://www.bandsintown.com/a/echo-hill" target="_blank" rel="noopener noreferrer" className="epk-pill">
              Bandsintown ↗
            </a>
          </div>
        </section>

        {/* Stage */}
        <section className="epk-section">
          <SectionHeading>Stage &amp; Tech</SectionHeading>
          <ul className="epk-tech">
            <li><span>Lineup</span>Gene Smith (vocals, guitar), Curtis Harvey (guitar), Amy Weaver (vocals, bass), Steve DeCristofaro (drums)</li>
            <li><span>Instruments</span>2 electric guitars, electric bass, drum kit</li>
            <li><span>Vocal mics</span>2 (Gene, Amy)</li>
          </ul>
        </section>

        {/* Socials */}
        <section className="epk-section">
          <SectionHeading>Follow</SectionHeading>
          <div className="epk-social-grid">
            {SOCIAL.map(({ label, handle, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="epk-social">
                <span className="epk-social-label">{label}</span>
                <span className="epk-social-handle">{handle}</span>
              </a>
            ))}
          </div>
        </section>

        {/* Booking */}
        <section className="epk-booking">
          <h2 className="epk-booking-title">Booking</h2>
          <p>For booking, press, and interview requests, email us.</p>
          <a href={BOOKING_MAILTO} className="epk-btn epk-btn--large">{BOOKING_EMAIL}</a>
        </section>
      </div>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <>
      <h2 className="epk-heading">{children}</h2>
      <div className="epk-heading-rule" />
    </>
  );
}

function LiteYouTube({ id, title }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="epk-video">
      <div className="epk-video-frame">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${id}?autoplay=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button className="epk-video-thumb" onClick={() => setPlaying(true)} aria-label={`Play ${title}`}>
            <img
              src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`}
              alt={title}
              loading="lazy"
            />
            <span className="epk-video-play">
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28"><path d="M8 5v14l11-7z" /></svg>
            </span>
          </button>
        )}
      </div>
      <p className="epk-video-title">{title}</p>
    </div>
  );
}
